/**
 * Sanitizes markdown content to fix common formatting issues
 * Removes wrapping code blocks and cleans up backticks
 *
 * @param markdown - The markdown text to sanitize
 * @returns Cleaned markdown text
 */
export const sanitizeMarkdown = (markdown: string): string => {
  // Remove wrapping markdown code block if present
  let sanitized = markdown.replace(
    /^```\s*(?:markdown)?\n([\s\S]*?)\n```$/g,
    '$1',
  );
  // Remove any other code blocks
  sanitized = sanitized.replace(/```[\s\S]*?```/g, '');
  // Remove any inline code backticks - preserve the content inside
  sanitized = sanitized.replace(/`([^`]*)`/g, '$1');
  // Remove any remaining backticks
  sanitized = sanitized.replace(/`/g, '');
  // Replace em dashes with comma and space
  sanitized = sanitized.replace(/—/g, ', ');
  // Trim any extra whitespace that might have been left
  return sanitized.trim();
};

/**
 * Replaces em dashes with comma and space
 *
 * @param text - The text containing em dashes
 * @returns Text with em dashes replaced
 */
export const replaceEmDashes = (text: string): string => {
  if (!text) return '';
  return text.replace(/—/g, ', ');
};

/**
 * Fixes malformed citation references in markdown text
 * Handles both UUID-based and numeric timestamp references
 *
 * @param text - The text containing potentially malformed references
 * @returns Text with fixed reference syntax
 *
 * @example
 * fixMalformedReferences("[→](uuid@123]") // Returns "[→](uuid@123)"
 * fixMalformedReferences("[→](47]")      // Returns "[→](47)"
 */
export const fixMalformedReferences = (text: string): string => {
  if (!text) return '';

  // Replace em dashes first
  let fixedText = replaceEmDashes(text);

  // Fix UUID-based references with missing closing parenthesis: [→](uuid@timestamp] -> [→](uuid@timestamp)
  fixedText = fixedText.replace(
    /\[→\]\(([a-f0-9-]+@\d+(?:\.\d+)?)\]/g,
    '[→]($1)',
  );

  // Fix regular timestamp references with bracket closing: [→](47] -> [→](47)
  fixedText = fixedText.replace(/\[→\]\((\d+(?:\.\d+)?)\]/g, '[→]($1)');

  // Remove horizontal spaces (not newlines) before and after any markdown link reference
  // Pattern matches: [any text](fileId@timestamp) or [any text](timestamp)
  // This removes spacing so text flows naturally when selected
  // Use [ \t]+ instead of \s+ to preserve newlines
  fixedText = fixedText.replace(
    /[ \t]+(\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\))[ \t]+/g,
    '$1',
  );
  fixedText = fixedText.replace(
    /[ \t]+(\[[^\]]+\]\(\d+(?:\.\d+)?\))[ \t]+/g,
    '$1',
  );
  fixedText = fixedText.replace(
    /[ \t]+(\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\))/g,
    '$1',
  );
  fixedText = fixedText.replace(/[ \t]+(\[[^\]]+\]\(\d+(?:\.\d+)?\))/g, '$1');
  fixedText = fixedText.replace(
    /(\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\))[ \t]+/g,
    '$1',
  );
  fixedText = fixedText.replace(/(\[[^\]]+\]\(\d+(?:\.\d+)?\))[ \t]+/g, '$1');

  return fixedText;
};

/**
 * Cleans up citation formatting by removing extra spaces and line breaks
 * Used to normalize citation arrows in markdown content
 *
 * @param text - The text containing citations
 * @returns Text with normalized citation formatting
 */
export const sanitizeCitations = (text: string): string => {
  // Replace em dashes first
  const sanitized = replaceEmDashes(text);

  // Remove horizontal spaces (not newlines) before and after any markdown link reference
  // Pattern matches: [any text](fileId@timestamp) or [any text](timestamp)
  // This removes spacing so text flows naturally when selected, as if references weren't there
  // Use [ \t]+ instead of \s+ to preserve newlines
  return sanitized
    .replace(/[ \t]+(\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\))[ \t]+/g, '$1')
    .replace(/[ \t]+(\[[^\]]+\]\(\d+(?:\.\d+)?\))[ \t]+/g, '$1')
    .replace(/[ \t]+(\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\))/g, '$1')
    .replace(/[ \t]+(\[[^\]]+\]\(\d+(?:\.\d+)?\))/g, '$1')
    .replace(/(\[[^\]]+\]\([a-f0-9-]+@\d+(?:\.\d+)?\))[ \t]+/g, '$1')
    .replace(/(\[[^\]]+\]\(\d+(?:\.\d+)?\))[ \t]+/g, '$1')
    .trim();
};
